<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import BackgroundSection from "./BackgroundSection.vue";
import content from "../content.js";

const projects = content.projects;

// Carousel state
const currentIndex = ref(0);
const itemsPerView = ref(3);
const isTransitioning = ref(false);

// Calculate total slides based on items per view
const totalSlides = computed(() => {
    return Math.ceil(projects.items.length - itemsPerView.value + 1);
});

// Update items per view based on screen size
const updateItemsPerView = () => {
    if (window.innerWidth < 640) {
        itemsPerView.value = 1;
    } else if (window.innerWidth < 1024) {
        itemsPerView.value = 2;
    } else {
        itemsPerView.value = 3;
    }
};

// Navigation functions
const nextSlide = () => {
    if (isTransitioning.value) return;
    isTransitioning.value = true;
    if (currentIndex.value < projects.items.length - itemsPerView.value) {
        currentIndex.value++;
    } else {
        currentIndex.value = 0;
    }
    setTimeout(() => {
        isTransitioning.value = false;
    }, 500);
};

const prevSlide = () => {
    if (isTransitioning.value) return;
    isTransitioning.value = true;
    if (currentIndex.value > 0) {
        currentIndex.value--;
    } else {
        currentIndex.value = projects.items.length - itemsPerView.value;
    }
    setTimeout(() => {
        isTransitioning.value = false;
    }, 500);
};

const goToSlide = (index) => {
    if (isTransitioning.value) return;
    isTransitioning.value = true;
    currentIndex.value = index;
    setTimeout(() => {
        isTransitioning.value = false;
    }, 500);
};

// Calculate transform for carousel
const carouselTransform = computed(() => {
    const slideWidth = 100 / itemsPerView.value;
    return `translateX(-${currentIndex.value * slideWidth}%)`;
});

// Auto-play carousel
let autoPlayInterval = null;

const startAutoPlay = () => {
    autoPlayInterval = setInterval(() => {
        nextSlide();
    }, 5000);
};

const stopAutoPlay = () => {
    if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
    }
};

onMounted(() => {
    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    startAutoPlay();
});

onUnmounted(() => {
    window.removeEventListener("resize", updateItemsPerView);
    stopAutoPlay();
});
</script>

<template>
    <BackgroundSection
        section-id="projects"
        :video="projects.video"
        :image="projects.image"
        overlay-class="bg-black/60"
    >
        <div class="max-w-6xl mx-auto px-6 py-16">
            <h2
                class="text-2xl sm:text-4xl md:text-5xl font-bold mb-8 sm:mb-12 text-center text-white drop-shadow-lg"
                data-aos="fade-up"
            >
                {{ projects.title }}
            </h2>

            <!-- Carousel Container -->
            <div
                class="relative"
                @mouseenter="stopAutoPlay"
                @mouseleave="startAutoPlay"
            >
                <!-- Navigation Arrows -->
                <button
                    @click="prevSlide"
                    class="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white/20 backdrop-blur-md border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 shadow-lg"
                    aria-label="Previous slide"
                >
                    <svg
                        class="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </button>

                <button
                    @click="nextSlide"
                    class="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white/20 backdrop-blur-md border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300 shadow-lg"
                    aria-label="Next slide"
                >
                    <svg
                        class="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>

                <!-- Carousel Track -->
                <div class="overflow-hidden mx-8">
                    <div
                        class="flex transition-transform duration-500 ease-in-out"
                        :style="{ transform: carouselTransform }"
                    >
                        <div
                            v-for="(project, index) in projects.items"
                            :key="index"
                            class="flex-shrink-0 px-3"
                            :style="{
                                width: `${100 / itemsPerView}%`,
                            }"
                            data-aos="fade-up"
                            :data-aos-delay="index * 100"
                        >
                            <!-- Project Card -->
                            <div
                                class="bg-white rounded-2xl overflow-hidden shadow-xl transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
                            >
                                <!-- Image -->
                                <div class="relative h-48 sm:h-56 overflow-hidden">
                                    <img
                                        :src="project.image"
                                        :alt="project.title"
                                        class="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                    />
                                </div>

                                <!-- Content -->
                                <div class="p-5 bg-white">
                                    <h3
                                        class="text-xl font-bold text-gray-900 mb-3"
                                    >
                                        {{ project.title }}
                                    </h3>
                                    <div class="space-y-2 text-gray-700">
                                        <p class="flex items-center gap-2">
                                            <span class="text-green-600 font-medium">Size:</span>
                                            {{ project.size }}
                                        </p>
                                        <p class="flex items-center gap-2">
                                            <span class="text-green-600 font-medium">Year Started:</span>
                                            {{ project.yearStarted }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination Dots -->
            <div class="flex justify-center gap-2 mt-8">
                <button
                    v-for="(_, index) in Math.max(1, projects.items.length - itemsPerView + 1)"
                    :key="index"
                    @click="goToSlide(index)"
                    class="w-3 h-3 rounded-full transition-all duration-300"
                    :class="
                        currentIndex === index
                            ? 'bg-white scale-125'
                            : 'bg-white/40 hover:bg-white/60'
                    "
                    :aria-label="`Go to slide ${index + 1}`"
                />
            </div>
        </div>
    </BackgroundSection>
</template>
